import {computed, effect, inject, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {ECalculusStateFilter} from '../../../config/state-filter.enum';
import {
  ICart,
  IClient,
  IInvoice,
  IPdfOverlay,
  IProcessOverviewState,
  IProcessState,
  IShownTableTab,
  tableIconMap
} from './state.interface';
import {Subject} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TShownTables} from '../shown-tables.type';
import {IReplacedClientForCartEvent} from './event.interface';
import {debounceTime} from 'rxjs/operators';
import {ControlStore, IControlObject} from 'frontier';

const LOCALSTORAGE_KEY: string = 'processOverviewState';

const localStorageState: IProcessOverviewState = ((): IProcessOverviewState => {
  try {
    return JSON.parse(localStorage.getItem(LOCALSTORAGE_KEY));
  } catch {
    return null;
  }
})();

const INITIAL_STATE: IProcessOverviewState = {
  selectedTableIndices: {
    ETLnMapping: 0,
    arrangeInvoice: 1,
    monitorPayment: 1,
  },
  selectedProcess: {
    category: ECalculusStateFilter.total,
    state: null,
  },
  selectedInvoice: null,
  selectedCart: null,
  selectedClient: null,
  selectedDocumentOutbox: null,
  pdfOverlay: null,
  isPdfLoading: false,
  detailsTabIndex: null,
  internalNote: '',
}

@Injectable({
  providedIn: 'root'
})
export class ProcessOverviewStore {
  private _controlStore: ControlStore = inject(ControlStore);
  private state: WritableSignal<IProcessOverviewState> = signal<IProcessOverviewState>(
      localStorageState ? {
        ...INITIAL_STATE,
        ...localStorageState,
        selectedTableIndices: {...INITIAL_STATE.selectedTableIndices, ...localStorageState.selectedTableIndices},
        selectedProcess: {...INITIAL_STATE.selectedProcess, ...localStorageState.selectedProcess},
      } : INITIAL_STATE
  );

  // selectors
  selectedTableInEtlnMapping: Signal<number> = computed((): number => this.state().selectedTableIndices.ETLnMapping);
  selectedTableInArrangeInvoice: Signal<number> = computed((): number => this.state().selectedTableIndices.arrangeInvoice);
  selectedTableInMonitorPayment: Signal<number> = computed((): number => this.state().selectedTableIndices.monitorPayment);
  selectedProcess: Signal<IProcessState> = computed((): IProcessState => this.state().selectedProcess);
  selectedInvoice: Signal<IInvoice> = computed((): IInvoice => this.state().selectedInvoice, {
    equal: (current: IInvoice, next: IInvoice): boolean => {
      return current?.obj?.signature === next?.obj?.signature && current?.invoiceId === next?.invoiceId;
    }
  });
  selectedDocumentOutbox: Signal<IControlObject> = computed((): IControlObject => this.state().selectedDocumentOutbox,
    {equal: (current: IControlObject, next: IControlObject): boolean => current?.signature === next?.signature})
  selectedCart: Signal<ICart> = computed((): ICart => this.state().selectedCart,
    {equal: (current: ICart, next: ICart): boolean => (current?.replaced === next?.replaced && current?.obj?.signature === next?.obj?.signature)});
  selectedClient: Signal<IClient> = computed((): IClient => this.state().selectedClient,
    {equal: (current: IClient, next: IClient): boolean => current?.replaced === next?.replaced  && current?.obj?.signature === next?.obj?.signature});
  pdfOverlay: Signal<IPdfOverlay> = computed((): IPdfOverlay => this.state().pdfOverlay);
  isPdfLoading: Signal<boolean> = computed((): boolean => this.state().isPdfLoading);
  detailsTabIndex: Signal<number> = computed((): number => this.state().detailsTabIndex);
  internalNote: Signal<string> = computed((): string => this.state().internalNote)
  tableTabs: Signal<Set<TShownTables>> = computed((): Set<TShownTables> => {
    const tabs: Set<TShownTables> = new Set<TShownTables>();
    const category: ECalculusStateFilter = this.selectedProcess().category;

    // carts is always show-able
    tabs.add('carts');

    switch (category) {
      case ECalculusStateFilter.ETLnMapping:
        tabs.add('clients');
        break;
      case ECalculusStateFilter.arrangeInvoice:
        tabs.add('documentOutbox');
        break;
      case ECalculusStateFilter.monitorPayment:
        tabs.add('openItems');
        break;
    }

    return tabs;
  })

  tableTabsArray: Signal<IShownTableTab[]> = computed((): IShownTableTab[] => {
    return Array.from(this.tableTabs()).map((tableName: TShownTables): IShownTableTab => {
      return {icon: tableIconMap.get(tableName), tableName};
    });
  })

  selectedTableIndex: Signal<number> = computed((): number => {
    const selectedProcess: IProcessState = this.selectedProcess();
    switch (selectedProcess.category) {
      case ECalculusStateFilter.ETLnMapping:
        return this.selectedTableInEtlnMapping();
      case ECalculusStateFilter.arrangeInvoice:
        return this.selectedTableInArrangeInvoice();
      case ECalculusStateFilter.monitorPayment:
        return this.selectedTableInMonitorPayment();
    }
    return 0;
  })

  isOnlyCartsTableDisplayed: Signal<boolean> = computed((): boolean => {
    return this.tableTabsArray()[this.selectedTableIndex()].tableName === 'carts'
  })

  isClientsTableDisplayed: Signal<boolean> = computed((): boolean => {
    return this.tableTabsArray()[this.selectedTableIndex()].tableName === 'clients'
  })

  // actions / sources
  selectedTableInEtlnMapping$: Subject<number> = new Subject<number>();
  selectedTableInArrangeInvoice$: Subject<number> = new Subject<number>();
  selectedTableInMonitorPayment$: Subject<number> = new Subject<number>();
  selectedProcessCategory$: Subject<ECalculusStateFilter> = new Subject<ECalculusStateFilter>();
  selectedProcessState$: Subject<IProcessState> = new Subject<IProcessState>();
  selectedInvoice$: Subject<IInvoice> = new Subject<IInvoice>();
  selectedCart$: Subject<ICart> = new Subject<ICart>();
  selectedClient$: Subject<IClient> = new Subject<IClient>();
  selectedDocumentOutbox$: Subject<IControlObject> = new Subject<IControlObject>();
  openedInvoiceIdUrl$: Subject<IInvoice> = new Subject<IInvoice>();
  pdfOverlay$: Subject<IPdfOverlay> = new Subject<IPdfOverlay>();
  replacedClientForCart$: Subject<IReplacedClientForCartEvent> = new Subject<IReplacedClientForCartEvent>();
  detailsTabIndex$: Subject<number> = new Subject<number>();
  internalNote$: Subject<string> = new Subject<string>();
  isPdfLoading$: Subject<boolean> = new Subject<boolean>();

  constructor() {
    // Store in localstorage
    effect((): void => {
      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(this.state()));
      console.log('State saved to localstorage', this.state());
    });

    this._controlStore.tenantChanged$.pipe(
      takeUntilDestroyed(),
    ).subscribe((): void => {
      this.state.update((state: IProcessOverviewState): IProcessOverviewState => {
        return {
          ...state,
          selectedClient: null,
          selectedInvoice: null,
          selectedCart: null,
        }
      });
    })

    // reducers
    this.selectedTableInEtlnMapping$
      .pipe(takeUntilDestroyed())
      .subscribe((value: number): void => {
        this.state.update((state: IProcessOverviewState): IProcessOverviewState => {
          return {
            ...state,
            selectedTableIndices: {
              ...state.selectedTableIndices,
              ETLnMapping: value,
            },
          };
        });
      });

    this.selectedTableInArrangeInvoice$
      .pipe(takeUntilDestroyed())
      .subscribe((value: number): void => {
        this.state.update((state: IProcessOverviewState): IProcessOverviewState => {
          return {
            ...state,
            selectedTableIndices: {
              ...state.selectedTableIndices,
              arrangeInvoice: value,
            },
          };
        });
      });

    this.selectedTableInMonitorPayment$
      .pipe(takeUntilDestroyed())
      .subscribe((value: number): void => {
        this.state.update((state: IProcessOverviewState): IProcessOverviewState => {
          return {
            ...state,
            selectedTableIndices: {
              ...state.selectedTableIndices,
              monitorPayment: value,
            },
          };
        });
      });

    this.selectedProcessCategory$
      .pipe(takeUntilDestroyed())
      .subscribe((value: ECalculusStateFilter): void => {
        this.state.update((state: IProcessOverviewState): IProcessOverviewState => {
          return {
            ...state,
            selectedCart: null,
            pdfOverlay: null,
            selectedClient: null,
            selectedInvoice: null,
            selectedProcess: {
              category: value,
              state: null,
            },
          };
        });
      });

    this.selectedProcessState$
      .pipe(takeUntilDestroyed())
      .subscribe((value: IProcessState): void => {
        this.state.update((state: IProcessOverviewState): IProcessOverviewState => {
          return {
            ...state,
            selectedCart: null,
            pdfOverlay: null,
            selectedClient: null,
            selectedInvoice: null,
            selectedProcess: value,
          };
        });
      });

    this.selectedInvoice$
      .pipe(takeUntilDestroyed())
      .subscribe((value: IInvoice): void => {
        this.state.update((state: IProcessOverviewState): IProcessOverviewState => {
          return {
            ...state,
            selectedInvoice: value,
          };
        });
      });

    this.selectedCart$
      .pipe(
        takeUntilDestroyed(),
        debounceTime(50),
      )
      .subscribe((value: ICart): void => {
        this.state.update((state: IProcessOverviewState): IProcessOverviewState => {
          return {
            ...state,
            selectedCart: value,
          };
        });
      });

    this.selectedClient$
      .pipe(takeUntilDestroyed())
      .subscribe((value: IClient): void => {
        this.state.update((state: IProcessOverviewState): IProcessOverviewState => {
          return {
            ...state,
            selectedClient: value,
          };
        });
      });

    this.selectedDocumentOutbox$
      .pipe(takeUntilDestroyed())
      .subscribe((value: IControlObject): void => {
        this.state.update((state: IProcessOverviewState): IProcessOverviewState => {
          return {
            ...state,
            selectedDocumentOutbox: value,
          };
        });
      });

    this.openedInvoiceIdUrl$
      .pipe(takeUntilDestroyed())
      .subscribe((value: IInvoice): void => {
        this.state.update((state: IProcessOverviewState): IProcessOverviewState => {
          return {
            ...state,
            selectedClient: null,
            selectedCart: null,
            selectedProcess: {
              category: ECalculusStateFilter.monitorPayment,
              state: null,
            },
            selectedInvoice: value,
          };
        });
      });

    this.isPdfLoading$.pipe(
      takeUntilDestroyed()
    ).subscribe((value: boolean): void => {
      this.state.update((state: IProcessOverviewState): IProcessOverviewState => {
        return {
          ...state,
          isPdfLoading: value,
        };
      })
    })

    this.pdfOverlay$
      .pipe(takeUntilDestroyed())
      .subscribe((value: IPdfOverlay): void => {
        this.state.update((state: IProcessOverviewState): IProcessOverviewState => {
          return {
            ...state,
            pdfOverlay: value,
          };
        });
      });

    this.detailsTabIndex$.pipe(
      takeUntilDestroyed()
    ).subscribe((value: number): void => {
      this.state.update((state: IProcessOverviewState): IProcessOverviewState => {
        return {
          ...state,
          detailsTabIndex: value,
        };
      });
    });

    this.internalNote$.pipe(
      takeUntilDestroyed()
    ).subscribe((value: string): void => {
      this.state.update((state: IProcessOverviewState): IProcessOverviewState => {
        return {
          ...state,
          internalNote: value,
        };
      });
    });

    this.replacedClientForCart$.pipe(
      takeUntilDestroyed()
    ).subscribe((evt: IReplacedClientForCartEvent): void => {
      if (this.isOnlyCartsTableDisplayed()) {
        this.selectedCart$.next({
          obj: evt.cart,
          number: (evt.cart as ICart).number,
          replaced: true,
        });
      }
      if (this.isClientsTableDisplayed()) {
        this.selectedClient$.next({
          obj: evt.client,
          name: evt.client.fullname,
          replaced: true,
        });
      }
    })
  }
}
